import React, { useCallback, useMemo, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { ClassNames } from "@emotion/react";
import styled from "@emotion/styled";
import { Input, Pagination } from "antd5";
import { isEmpty } from "lodash";

import FilterDrawer from "components/filter_form/filter_drawer/FilterDrawer";
import Button from "components/ui/button/Button";
import { useTeams } from "lib/hooks/api/teams/useTeams";
import { useTablePageSize } from "lib/hooks/useTablePageSize";
import { useURLState } from "lib/hooks/useURLState";
import { SortState } from "lib/search/types";
import { sysPrimaryDefault } from "lib/themes/colors";
import { OpportunitySortBy, OpportunitySortOrder } from "lib/types/graphQLEnums";
import EditColumnsButton from "../../lib/core_components/EditColumnsButton";
import { useDebouncedValue } from "../../lib/debounce";
import { PaginationOptions } from "../../lib/generated/app-service-gql/graphql";
import { useOpportunities } from "../../lib/hooks/api/opportunities/useOpportunities";
import { EventDataTypes } from "../../lib/tracking";
import { ColumnSetting, TableSettings } from "../../lib/types/models";
import { Flex } from "../../styles/utility-components";
import OpportunitiesActionBar from "./opportunities_action_bar/OpportunitiesActionBar";
import ExportOpportunitiesButton from "./opportunities_table/ExportOpportunitiesButton";
import { OpportunitiesTable, OpportunityColumns } from "./opportunities_table/OpportunitiesTable";
import { mapSortFieldToEnum, mapSortOrderToEnum } from "./utils/sort";
import { AssigneeDropdownFilter } from "./AssigneeDropdownFilter";
import { FilterForm } from "./FilterForm";
import { QuickFilterBar } from "./QuickFilterBar";
import { TeamDropdownFilter } from "./TeamDropdownFilter";
import {
  ALL_AVAILABLE_OPPORTUNITY_COLUMNS,
  convertOpportunityFiltersToSearchRequest,
  EMPTY_OPPORTUNITY_FILTERS,
  OpportunityFilters,
  parseOpportunitiesUrlState,
  parsePaginationUrlState,
} from "./utils";

type FilterableOpportunitiesProps = {
  allAvailableColumns?: ColumnSetting<OpportunityColumns>[];
  defaultFilters: OpportunityFilters;
};

export type SelectedOpportunity = { id: string; stageId: string; name: string };

export function FilterableOpportunities({
  allAvailableColumns = ALL_AVAILABLE_OPPORTUNITY_COLUMNS,
  defaultFilters,
}: FilterableOpportunitiesProps) {
  const { data: teams } = useTeams();

  const [filters = defaultFilters, setOppFilters] = useURLState<OpportunityFilters>(
    "opportunityFilters",
    defaultFilters,
    parseOpportunitiesUrlState,
  );
  const [pageSize, setPageSize] = useTablePageSize("opportunities-table");
  const [pagination = { page: 1, pageSize }, setPagination] = useURLState<PaginationOptions>(
    "pagination",
    { page: 1, pageSize },
    parsePaginationUrlState,
  );
  const [debouncedFilters] = useDebouncedValue(filters, 500);
  const [debouncedPagination, updateDebouncedPagination] = useDebouncedValue(pagination, 500);
  const [selectedOpportunities, setSelectedOpportunities] = useState<SelectedOpportunity[]>([]);
  const selectedOpportunityIds = useMemo(
    () => selectedOpportunities.map((opp) => opp.id),
    [selectedOpportunities],
  );

  const [tableColumns, setTableColumns] = useState<ColumnSetting<OpportunityColumns>[] | undefined>(
    undefined,
  );

  const setFilters = useCallback(
    (val: OpportunityFilters) => {
      setOppFilters(val);
      setPagination({ page: 1, pageSize });
    },
    [setOppFilters, setPagination, pageSize],
  );

  const appliedColumns: ColumnSetting<OpportunityColumns>[] = tableColumns
    ? tableColumns
    : [...allAvailableColumns];

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [activeGroups, setActiveGroups] = useState<string[]>([]);

  const { data, isLoading, isError } = useOpportunities(
    convertOpportunityFiltersToSearchRequest(debouncedFilters),
    debouncedPagination,
    { enabled: !isEmpty(debouncedFilters) },
  );

  const handleSelectionChange = (selectedIds: string[]) => {
    if (!data?.results) return;

    const selectedOpportunitiesWithStages = selectedIds.map((id) => {
      const opportunity = data.results.find((opp) => opp.id === id);
      return {
        id,
        stageId: opportunity?.stage.id || "",
        name: opportunity?.name || "",
      };
    });
    setSelectedOpportunities(selectedOpportunitiesWithStages);
  };

  const handleClearSelection = () => {
    setSelectedOpportunities([]);
  };

  const handleSortChange = useCallback(
    (newSort: SortState) => {
      setFilters({
        ...filters,
        orderBy: mapSortFieldToEnum(newSort.field),
        order: mapSortOrderToEnum(newSort.order),
      });
    },
    [filters, setFilters],
  );

  return (
    <Wrapper column gap={16}>
      <Flex gap={8} aria-label="Opportunity action items">
        <ClassNames>
          {({ css }) => (
            <Input
              placeholder="Search"
              prefix={<SearchOutlined />}
              value={filters?.searchText ?? ""}
              onChange={(e) => {
                setFilters({ ...filters, searchText: e.target.value });
              }}
              allowClear
              className={css({
                maxWidth: 350,
              })}
            />
          )}
        </ClassNames>
        <AssigneeDropdownFilter
          value={filters?.assignee ? filters.assignee : { ids: [], includeNulls: false }}
          onChange={(value) => setFilters({ ...filters, assignee: value })}
        />
        {teams && teams.length > 1 && (
          <TeamDropdownFilter
            teamIds={filters.teamIds ?? []}
            onChange={(teamIds) => setFilters({ ...filters, teamIds: teamIds })}
          />
        )}
        <Button
          as="button"
          buttonType="secondary"
          onClick={() => setIsFilterDrawerOpen(true)}
          icon="filterFunnel02"
          iconProps={{ color: sysPrimaryDefault }}
        >
          All filters
        </Button>
      </Flex>
      <Flex gap={8} alignItems="center" justifyContent="space-between">
        <QuickFilterBar
          filters={filters}
          isLoading={isLoading}
          totalResults={data?.total ?? 0}
          onClearFilter={(key) => setFilters({ ...filters, [key]: undefined })}
          onFilterClick={() => setIsFilterDrawerOpen(true)}
          clearAllFilters={() =>
            setFilters({ ...EMPTY_OPPORTUNITY_FILTERS, searchText: filters.searchText })
          }
        />
        <Flex gap={8}>
          <EditColumnsButton<TableSettings<OpportunityColumns>>
            allAvailableColumns={[...ALL_AVAILABLE_OPPORTUNITY_COLUMNS]}
            selectedColumns={appliedColumns}
            onNewColumnSettingChange={(c) => {
              setTableColumns([...c]);
            }}
            dataType={EventDataTypes.opportunity}
          />
          <ExportOpportunitiesButton
            filters={debouncedFilters}
            selectedOpportunities={selectedOpportunityIds}
            totalCount={data?.total ?? 0}
            isLoading={isLoading}
          />
        </Flex>
      </Flex>
      {selectedOpportunities.length > 0 && (
        <OpportunitiesActionBar
          selectedOpportunities={selectedOpportunities}
          onClearSelection={handleClearSelection}
        />
      )}
      <OpportunitiesTable
        opportunities={data?.results}
        columnSettings={appliedColumns.map((c) => c.field)}
        isLoading={isLoading}
        isError={isError}
        sort={{
          field: filters?.orderBy || OpportunitySortBy.CreatedAt,
          order: filters?.order || OpportunitySortOrder.Desc,
        }}
        onSortChange={handleSortChange}
        onSelectionChange={handleSelectionChange}
        selectedRowKeys={selectedOpportunityIds}
        onClearFilters={() => setFilters(EMPTY_OPPORTUNITY_FILTERS)}
      />
      <Pagination
        current={pagination.page}
        pageSize={pagination.pageSize}
        onChange={(page, pageSize) => {
          setPageSize(pageSize);
          setPagination({ page, pageSize });
          updateDebouncedPagination({ page, pageSize });
        }}
        total={data?.total}
        showSizeChanger
        align="center"
      />
      <FilterDrawer open={isFilterDrawerOpen} onClose={() => setIsFilterDrawerOpen(false)}>
        {isFilterDrawerOpen && (
          <FilterForm
            onClose={() => setIsFilterDrawerOpen(false)}
            onChange={setFilters}
            filters={filters ?? EMPTY_OPPORTUNITY_FILTERS}
            activeGroups={activeGroups}
            onActiveGroupsChange={setActiveGroups}
            hiddenFilters={teams && teams.length > 1 ? [] : ["team"]}
          />
        )}
      </FilterDrawer>
    </Wrapper>
  );
}

const Wrapper = styled(Flex)({
  paddingBottom: 24,
  width: "100%",
});
